import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faMusic } from '@fortawesome/free-solid-svg-icons';
import { fetchSongs } from '../../../../../services/songService';
import { createSetlist, updateSetlist } from '../../../../../services/setlistService';
import { updateEvent } from '../../../../../services/eventService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { DEFAULT_SONG_ROLE } from '../../../../../constants/songRoles'; // Import the constant
import '../../styles/MobileEventView.css';

// Custom styles to ensure dark theme throughout the modal
const modalStyles = `
  .updated-event-view .modal-content {
    background-color: #121212 !important;
    border: 1px solid #333 !important;
    border-radius: 0.3rem !important;
    overflow: hidden !important;
  }
  .modal-backdrop {
    background-color: rgba(0, 0, 0, 0.8) !important;
  }
  .modal-header {
    background-color: #121212 !important;
    border-color: #333 !important;
  }
  .modal-footer {
    background-color: #121212 !important;
    border-color: #333 !important;
  }
  
  /* Fix for the white space at bottom */
  .updated-event-view .modal-body {
    background-color: #121212 !important;
    min-height: 100% !important;
    padding-bottom: 0 !important;
    padding-top: 0 !important;
  }
  
  /* Ensure the song-selection div fills the available space */
  .updated-event-view .song-selection {
    background-color: #121212 !important;
    overflow-y: auto !important;
    height: calc(100vh - 180px) !important; /* Account for search + footer */
  }
  
  /* Fixed elements styling */
  .updated-event-view .fixed-search-container {
    background-color: #121212 !important;
  }
`;

const MobileSetlistModal = ({
  show,
  onHide,
  event,
  setlist,
  onSave
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [selectedSongs, setSelectedSongs] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [savedSuccessfully, setSavedSuccessfully] = useState(false);

  // Load songs
  useEffect(() => {
    const loadSongs = async () => {
      if (!show) return; // Only load when modal is shown
      
      setLoading(true);
      setError('');
      try {
        // Get community ID from the event
        const communityId = event?.community_id;
        
        // Fetch all songs
        const songData = await fetchSongs();
        
        // Filter songs based on community context
        let filteredSongs = [];
        if (communityId) {
          // Show community songs + user's private songs
          filteredSongs = songData.filter(song => 
            (song.owner_type === 'community' && parseInt(song.owner_id) === parseInt(communityId)) ||
            (song.owner_type === 'user')
          );
        } else {
          // Show only user's private songs
          filteredSongs = songData.filter(song => song.owner_type === 'user');
        }
        
        // Sort alphabetically by title
        filteredSongs.sort((a, b) => a.title.localeCompare(b.title));
        
        setSongs(filteredSongs);
        setFilteredSongs(filteredSongs);
        
        // Reset selected songs (clearing checkboxes)
        setSelectedSongs({});
      } catch (error) {
        setError('Napaka pri nalaganju pesmi');
      } finally {
        setLoading(false);
      }
    };
    
    loadSongs();
  }, [show, event]);

  // Extract available tags from songs
  const availableTags = (() => {
    const tagSet = new Set();
    songs.forEach(song => {
      if (song.tags) {
        song.tags.forEach(tag => tagSet.add(tag.name));
      }
    });
    return [...tagSet].sort();
  })();

  // Filter songs based on search term and selected tag
  useEffect(() => {
    if (!songs.length) {
      setFilteredSongs([]);
      return;
    }

    let filtered = songs;

    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(song =>
        (song.title && song.title.toLowerCase().includes(term)) ||
        (song.composer && song.composer.toLowerCase().includes(term))
      );
    }

    if (selectedTag) {
      filtered = filtered.filter(song =>
        song.tags && song.tags.some(tag => tag.name === selectedTag)
      );
    }

    setFilteredSongs(filtered);
  }, [songs, searchTerm, selectedTag]);

  // Handle song selection
  const handleSongSelect = (songId) => {
    setSelectedSongs(prev => ({
      ...prev,
      [songId]: !prev[songId]
    }));
  };

  // Handle saving selected songs
  const handleSave = async () => {
    const selectedSongIds = Object.keys(selectedSongs).filter(id => selectedSongs[id]);
    if (selectedSongIds.length === 0) {
      setError('Izberi vsaj eno pesem');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const setlistName = `${event.title} - ${event.date}`;
      
      // Prepare songs for setlist - selected songs come after existing ones
      let songsList = [];

      // Add existing songs from setlist if available
      if (setlist && setlist.songs) {
        // Include existing songs first
        songsList = [...setlist.songs];

        // Add all newly selected songs (duplicates are allowed)
        const newSongs = selectedSongIds.map((id, index) => {
          const songData = songs.find(s => s.id === parseInt(id));
          return {
            song_id: parseInt(id),
            title: songData?.title || 'Unknown Song',
            song_role: DEFAULT_SONG_ROLE,
            order: songsList.length + index
          };
        });

        songsList = [...songsList, ...newSongs];
      } else {
        // No existing setlist, create one with all selected songs
        songsList = selectedSongIds.map((id, index) => {
          const songData = songs.find(s => s.id === parseInt(id));
          return {
            song_id: parseInt(id),
            title: songData?.title || 'Unknown Song',
            song_role: DEFAULT_SONG_ROLE,
            order: index
          };
        });
      }
      
      const setlistData = {
        name: setlistName,
        event_id: event.id,
        songs: songsList,
        // Add community context if available
        community_id: event.community_id,
        owner_type: event.community_id ? 'community' : 'user',
        owner_id: event.community_id || user?.id
      };

      let updatedSetlist;

      if (event.setlist_id) {
        // Update existing setlist
        const response = await updateSetlist(event.setlist_id, setlistData);
        updatedSetlist = response?.setlist || { ...setlistData, id: event.setlist_id };
      } else {
        // Create new setlist and link to event
        const setlistResponse = await createSetlist(setlistData);
        updatedSetlist = setlistResponse?.setlist;
        
        if (setlistResponse?.setlist?.id) {
          // Link the setlist to the event
          await updateEvent(event.id, {
            ...event,
            setlist_id: setlistResponse.setlist.id,
            time: event.time && typeof event.time === 'string' && event.time.includes(':') 
              ? event.time.slice(0, 5)  // Get only HH:MM part
              : event.time
          });
        }
      }

      // Reset selected songs
      setSelectedSongs({});
      setSavedSuccessfully(true);
      
      if (typeof onSave === 'function') {
        // Pass the updated setlist to the parent component
        onSave(updatedSetlist || { ...setlistData, songs: songsList });
      }
      
      onHide();
    } catch (error) {
      setError("Napaka pri shranjevanju sporeda.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Inject the custom styles */}
      <style>{modalStyles}</style>
      
      <Modal 
        show={show} 
        onHide={onHide} 
        centered 
        fullscreen="sm-down"
        className="updated-event-view"
        contentClassName="bg-dark text-white"
      >
        {/* Fixed search bar at top */}
        <div className="fixed-search-container" style={{
          position: "sticky",
          top: 0,
          zIndex: 1040,
          padding: "12px 12px 8px 12px",
          background: "#121212",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
        }}>
          {error && (
            <div className="alert alert-danger py-2 mb-2">
              {error}
            </div>
          )}
          
          {/* Rounded Search input */}
          <div className="input-group" style={{ 
            borderRadius: "24px", 
            overflow: "hidden", 
            background: "#1d1d1d",
            border: "1px solid #333"
          }}>
            <span className="input-group-text bg-dark text-white border-0" style={{ background: "#1d1d1d !important" }}>
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <Form.Control
              type="text"
              placeholder="Išči pesmi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-dark text-white border-0"
              style={{ background: "#1d1d1d !important" }}
            />
          </div>

          {/* Tag filter pills */}
          {availableTags.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              paddingTop: '8px',
              paddingBottom: '2px',
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}>
              <button
                type="button"
                className={`btn btn-sm ${!selectedTag ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ borderRadius: '16px', whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                onClick={() => setSelectedTag('')}
              >
                Vse
              </button>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  className={`btn btn-sm ${selectedTag === tag ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ borderRadius: '16px', whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                  onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}
        </div>

        <Modal.Body className="p-0" style={{ paddingBottom: "0", paddingTop: "0" }}>
          {/* Song selection list with proper spacing for fixed header and footer */}
          <div className="song-selection" style={{ paddingBottom: "70px" }}>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : filteredSongs.length === 0 ? (
              <div className="text-center py-3 text-muted">
                {songs.length === 0 
                  ? "Ni razpoložljivih pesmi" 
                  : "Ni zadetkov za iskalni niz"}
              </div>
            ) : (
              <div className="event-setlist">
                {filteredSongs.map(song => (
                  <div 
                    key={song.id}
                    className="event-setlist-item"
                    onClick={() => handleSongSelect(song.id)}
                  >
                    <div className="event-setlist-item-left">
                      <div className="event-setlist-details">
                        <div className="event-setlist-title">{song.title}</div>
                        {song.composer && (
                          <div className="event-setlist-role">{song.composer}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="event-setlist-item-right">
                      {/* Checkbox */}
                      <Form.Check 
                        type="checkbox"
                        checked={!!selectedSongs[song.id]}
                        onChange={() => {}} // Handled by the container onClick
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal.Body>
        
        {/* Fixed bottom footer with rounded buttons */}
        <div 
          style={{ 
            position: "fixed", 
            bottom: 0, 
            left: 0, 
            right: 0, 
            padding: "15px", 
            background: "#121212", 
            zIndex: 1050,
            display: "flex",
            justifyContent: "space-between",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "0" // Ensure no rounded corners at bottom
          }}
        >
          <Button 
            variant="outline-secondary" 
            onClick={onHide} 
            style={{ 
              borderRadius: "24px",
              minWidth: "100px",
              fontWeight: "500"
            }}
          >
            Prekliči
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave} 
            disabled={Object.keys(selectedSongs).filter(id => selectedSongs[id]).length === 0 || saving}
            style={{ 
              borderRadius: "24px",
              minWidth: "140px",
              fontWeight: "500"
            }}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Shranjevanje...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faMusic} className="me-1" />
                Dodaj ({Object.keys(selectedSongs).filter(id => selectedSongs[id]).length})
              </>
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default MobileSetlistModal;